import { UUID } from 'crypto'
import { OrgData } from '@/models/Orgs'

const serverURL = import.meta.env.VITE_API_URL

const MISSING_ORG_UUID_MESSAGE =
  'Missing organisation information. Please refresh and try again.'

export interface InvoicePaymentRequest {
  org_uuid: UUID
  billing_legal_name: string
  billing_email: string
  billing_contact_name: string
  billing_contact_phone?: string
  billing_address_line1: string
  billing_address_line2?: string
  billing_city: string
  billing_state?: string
  billing_postal_code: string
  billing_country: string
  tax_registration_number?: string
  billing_frequency?: string
  discount_code?: string
  tier?: number
}

export interface DiscountCodeValidation {
  valid: boolean
  code?: string
  discount_type?: 'percentage'
  discount_value?: number
  duration_months?: number
  final_price?: number
  original_price?: number
  discount_amount?: number
  error_message?: string
}

export async function fetchCheckoutSession(orgUUID: UUID, token: string, tier?: number, billingFrequency: string = 'Monthly') {
  if (!orgUUID) {
    throw new Error('No user')
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const body: { org_uuid: UUID; tier?: number; billing_frequency: string } = { org_uuid: orgUUID, billing_frequency: billingFrequency }
  if (tier !== undefined) {
    body.tier = tier
  }

  const response = await fetch(
    `${serverURL}/subscriptions/create-checkout-session`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    }
  )
  if (!response.ok) {
    // Surface the upstream detail (e.g. Stripe's "No such customer …") rather
    // than swallowing it. Our error responses use `detail` as a string for
    // server-validated errors and stale-Stripe-state failures.
    const errorData = await response.json().catch(() => ({}))
    const detail = errorData.detail
    const message =
      typeof detail === 'string'
        ? detail
        : `Failed to create checkout session: ${response.status}`
    throw new Error(message)
  }

  const data = await response.json()
  return data
}

export async function updateSubscription(org: OrgData, token: string) {
  if (!org) {
    throw new Error('No org')
  }
  const customerResponse = await fetch(
    `${serverURL}/subscriptions/customer/${org.uuid}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  )

  if (!customerResponse.ok) {
    throw new Error(`HTTP error! status: ${customerResponse.status}`)
  }

  const customerId = await customerResponse.json()

  const customerPortalResponse = await fetch(
    `${serverURL}/subscriptions/create-portal-session`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: customerId.data[0].id,
        org_uuid: org.uuid,
      }),
    }
  )

  if (!customerPortalResponse.ok) {
    throw new Error(`HTTP error! status: ${customerPortalResponse.status}`)
  }

  const data = await customerPortalResponse.json()
  return data
}

export async function validateDiscountCode(
  code: string,
  originalPrice: number,
  frequencyMultiplier: number,
  token: string
): Promise<DiscountCodeValidation> {
  if (!code) {
    throw new Error('No discount code provided')
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(
    `${serverURL}/subscriptions/validate-discount-code?code=${encodeURIComponent(
      code
    )}&original_price=${originalPrice}&frequency_multiplier=${frequencyMultiplier}`,
    {
      method: 'POST',
      headers,
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.detail || `HTTP error! status: ${response.status}`
    )
  }

  const data = await response.json()
  return data
}

export async function createInvoiceSubscription(
  invoiceData: InvoicePaymentRequest,
  token: string
) {
  if (!invoiceData.org_uuid) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(
    `${serverURL}/subscriptions/create-invoice-subscription`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(invoiceData),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.detail || `HTTP error! status: ${response.status}`
    )
  }

  const data = await response.json()
  return data
}

export interface PauseStatusResponse {
  can_pause: boolean
  pauses_used_this_year: number
  pause_limit: number
  currently_paused: boolean
  pause_start_date: string | null
  pause_end_date: string | null
  days_remaining: number | null
  has_scheduled_pause: boolean
  scheduled_pause_start_date: string | null
  scheduled_pause_end_date: string | null
}

export interface PauseSubscriptionRequest {
  org_uuid: UUID
  pause_end_date: string
  pause_start_date?: string
  pause_reason?: string
}

export interface PauseSubscriptionResponse {
  success: boolean
  org_uuid: string
  pause_end_date: string
  message: string
}

export interface ResumeSubscriptionRequest {
  org_uuid: UUID
}

export interface ResumeSubscriptionResponse {
  success: boolean
  org_uuid: string
  resumed_at: string
  message: string
}

// FastAPI's automatic request-validation errors (422) return `detail` as an
// array of objects rather than a string, which `new Error()` would stringify
// to "[object Object]" — fall back to a generic message for anything that
// isn't the plain string these endpoints normally send.
function extractPauseErrorDetail(errorData: unknown, status: number): string {
  const detail = (errorData as { detail?: unknown } | undefined)?.detail
  return typeof detail === 'string' && detail
    ? detail
    : `HTTP error! status: ${status}`
}

export async function getPauseStatus(
  orgUUID: UUID,
  token: string
): Promise<PauseStatusResponse> {
  if (!orgUUID) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(
    `${serverURL}/subscriptions/pause-status?org_uuid=${orgUUID}`,
    {
      method: 'GET',
      headers,
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      extractPauseErrorDetail(errorData, response.status)
    )
  }

  const data = await response.json()
  return data
}

export async function pauseSubscription(
  pauseData: PauseSubscriptionRequest,
  token: string
): Promise<PauseSubscriptionResponse> {
  if (!pauseData.org_uuid) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(`${serverURL}/subscriptions/pause`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      org_uuid: String(pauseData.org_uuid),
      pause_end_date: pauseData.pause_end_date,
      pause_start_date: pauseData.pause_start_date,
      pause_reason: pauseData.pause_reason,
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      extractPauseErrorDetail(errorData, response.status)
    )
  }

  const data = await response.json()
  return data
}

export async function resumeSubscription(
  resumeData: ResumeSubscriptionRequest,
  token: string
): Promise<ResumeSubscriptionResponse> {
  if (!resumeData.org_uuid) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(`${serverURL}/subscriptions/resume`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      org_uuid: String(resumeData.org_uuid),
    }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      extractPauseErrorDetail(errorData, response.status)
    )
  }

  const data = await response.json()
  return data
}

export interface ApplyDiscountCodeResponse {
  success: boolean
  message: string
  code: string
  original_amount: number
  discount_amount: number
  final_amount: number
  applied_at?: string | null
  expires_at?: string | null
}

export async function applyDiscountCode(
  orgUUID: string,
  discountCode: string,
  token: string
): Promise<ApplyDiscountCodeResponse> {
  if (!orgUUID) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }
  if (!discountCode) {
    throw new Error('No discount code provided')
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(
    `${serverURL}/subscriptions/apply-discount-code/${orgUUID}`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({ discount_code: discountCode }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.detail || `HTTP error! status: ${response.status}`
    )
  }

  return response.json()
}

export interface RemoveDiscountCodeResponse {
  success: boolean
  message: string
  code?: string | null
}

export async function removeDiscountCode(
  orgUUID: string,
  token: string
): Promise<RemoveDiscountCodeResponse> {
  if (!orgUUID) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(
    `${serverURL}/subscriptions/discount-code/${orgUUID}`,
    {
      method: 'DELETE',
      headers,
    }
  )

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.detail || `HTTP error! status: ${response.status}`
    )
  }

  return response.json()
}

export class RequestUpgradeError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'RequestUpgradeError'
    this.status = status
  }
}

/**
 * Map a RequestUpgradeError to a user-facing message. The backend already
 * puts a precise wait time in the 429 detail, so surface that directly;
 * anything else (network error, 500, etc.) falls back to a generic message
 * rather than exposing a raw backend/HTTP error string.
 */
export function requestUpgradeErrorMessage(error: unknown): string {
  if (error instanceof RequestUpgradeError && error.status === 429) {
    return error.message
  }
  return 'Failed to send request. Please try again.'
}

export async function requestUpgrade(orgUUID: UUID, token: string) {
  if (!orgUUID) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(`${serverURL}/subscriptions/request-upgrade`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ org_uuid: String(orgUUID) }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new RequestUpgradeError(
      errorData.detail || `HTTP error! status: ${response.status}`,
      response.status,
    )
  }

  return response.json()
}

export async function cancelScheduledPause(
  orgUUID: UUID,
  token: string
): Promise<{ success: boolean; message: string }> {
  if (!orgUUID) {
    throw new Error(MISSING_ORG_UUID_MESSAGE)
  }

  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }

  const response = await fetch(`${serverURL}/subscriptions/cancel-scheduled-pause`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ org_uuid: String(orgUUID) }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      extractPauseErrorDetail(errorData, response.status)
    )
  }

  const data = await response.json()
  return data
}
